/**
 * TicketCard Context
 * 
 * Shared context for TicketCard components to communicate.
 * Separated into its own file to support Fast Refresh.
 * 
 * @internal
 */

import { createContext, useContext, type RefObject } from 'react';

// ============================================================================
// Context Definition
// ============================================================================

export interface TicketCardContextValue {
  /** Register a divider's position for punch-hole placement */
  registerPunchPosition: (element: HTMLElement | null) => void;
  /** The card's container ref for position calculations */
  cardRef: RefObject<HTMLDivElement | null>;
}

export const TicketCardContext = createContext<TicketCardContextValue | null>(null);

/**
 * Hook to access TicketCard context (used by TicketDivider)
 * Returns null if not within a TicketCard (safe to use outside)
 */
export function useTicketCardContext(): TicketCardContextValue | null {
  return useContext(TicketCardContext);
}

/**
 * Hook to access TicketCard context with requirement
 * Throws if not within a TicketCard
 */
export function useRequiredTicketCardContext(): TicketCardContextValue {
  const context = useContext(TicketCardContext);
  if (!context) {
    throw new Error('useRequiredTicketCardContext must be used within a TicketCard');
  }
  return context;
}
